import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import styles from './NetflixPlayer.module.css';

// Module-level "active player" tracking so global keyboard shortcuts route
// to the player the user last interacted with (instead of firing on every
// mounted player at once on a multi-clip review grid).
let activePlayerId = null;
let playerCounter = 0;

const formatTime = (s) => {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

// MediaRecorder-emitted WebM blobs leave duration as Infinity until the
// browser walks the cluster index. The fix: seek past the end, wait for
// the seeked event to fire (browser now knows the duration), then jump
// back to 0. This sets video.duration to a finite number.
/* eslint-disable no-param-reassign */
const normalizeWebmDuration = (videoEl) => {
  if (!videoEl) return;
  if (Number.isFinite(videoEl.duration) && videoEl.duration > 0) return;
  const onSeeked = () => {
    videoEl.removeEventListener('seeked', onSeeked);
    try { videoEl.currentTime = 0; } catch (e) { /* noop */ }
  };
  videoEl.addEventListener('seeked', onSeeked);
  try { videoEl.currentTime = 1e6; } catch (e) { /* noop */ }
};
/* eslint-enable no-param-reassign */

// SVG icons inlined so the player has no asset dependencies.
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 5v14l12-7z" /></svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>
);
const Skip10BackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
    <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">10</text>
  </svg>
);
const Skip10FwdIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 4v5h-5" />
    <text x="12" y="15" textAnchor="middle" fontSize="7" fontWeight="700" fill="currentColor" stroke="none">10</text>
  </svg>
);
const VolumeIcon = ({ muted, level }) => {
  if (muted || level === 0) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zM16.5 12l3-3-1.4-1.4L15 10.6l-3-3L10.6 9l3 3-3 3 1.4 1.4 3-3 3 3 1.4-1.4-3-3z" /></svg>
    );
  }
  if (level < 0.5) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zM15.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" /></svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>
  );
};
const FullscreenEnterIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>
);
const FullscreenExitIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" /></svg>
);

const HIDE_DELAY_MS = 2400;
const HOVER_SEEK_THROTTLE_MS = 90;

const NetflixPlayer = forwardRef(({
  src,
  poster,
  autoPlay = false,
  muted = false,
  playsInline = true,
  aspectRatio = '16 / 9',
  fillHeight = false,
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate,
  onLoadedMetadata,
  onDurationChange,
  className = '',
}, ref) => {
  const idRef = useRef(0);
  if (!idRef.current) {
    playerCounter += 1;
    idRef.current = playerCounter;
  }

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const thumbVideoRef = useRef(null);
  const thumbCanvasRef = useRef(null);
  const progressRef = useRef(null);
  const hideTimerRef = useRef(null);
  const scrubbingRef = useRef(false);
  const lastSeekRef = useRef(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(muted);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [hover, setHover] = useState(null); // { x: pixel-x, time: seconds }
  const [scrubbing, setScrubbing] = useState(false);
  // Container width — tracked via ResizeObserver. When the player is small
  // (e.g. embedded in a review card) we drop down to a "compact" control
  // layout: just play/pause + fullscreen + a thin progress strip pinned to
  // the bottom edge. The rich controls (skip-10, time, volume, hover
  // thumbnails) only appear when the player is large enough to host them
  // or when the user enters fullscreen.
  const [containerWidth, setContainerWidth] = useState(0);
  const COMPACT_WIDTH_PX = 520;
  const isCompact = containerWidth > 0 && containerWidth < COMPACT_WIDTH_PX && !isFullscreen;

  useImperativeHandle(ref, () => videoRef.current, []);

  // ── Auto-hide controls when playing ──────────────────────────────
  const armHideTimer = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (isPlaying && !scrubbingRef.current) {
      hideTimerRef.current = setTimeout(() => setShowControls(false), HIDE_DELAY_MS);
    }
  }, [isPlaying]);

  const revealControls = useCallback(() => {
    setShowControls(true);
    armHideTimer();
  }, [armHideTimer]);

  useEffect(() => {
    if (isPlaying) armHideTimer();
    else setShowControls(true);
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [isPlaying, armHideTimer]);

  // ── Video event wiring ───────────────────────────────────────────
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    const onTime = (e) => {
      setCurrentTime(v.currentTime || 0);
      // Buffered end (best-effort: last buffered range).
      try {
        const len = v.buffered.length;
        if (len > 0) setBufferedEnd(v.buffered.end(len - 1));
      } catch (err) { /* noop */ }
      if (onTimeUpdate) onTimeUpdate(e);
    };
    const onMeta = (e) => {
      if (Number.isFinite(v.duration) && v.duration > 0) setDuration(v.duration);
      else normalizeWebmDuration(v);
      if (onLoadedMetadata) onLoadedMetadata(e);
    };
    const onDur = (e) => {
      if (Number.isFinite(v.duration) && v.duration > 0) setDuration(v.duration);
      if (onDurationChange) onDurationChange(e);
    };
    const onPlayEv = (e) => { setIsPlaying(true); if (onPlay) onPlay(e); };
    const onPauseEv = (e) => { setIsPlaying(false); if (onPause) onPause(e); };
    const onEndedEv = (e) => { setIsPlaying(false); if (onEnded) onEnded(e); };
    const onWaiting = () => setIsBuffering(true);
    const onPlaying = () => setIsBuffering(false);
    const onCanPlay = () => setIsBuffering(false);
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('durationchange', onDur);
    v.addEventListener('play', onPlayEv);
    v.addEventListener('pause', onPauseEv);
    v.addEventListener('ended', onEndedEv);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('playing', onPlaying);
    v.addEventListener('canplay', onCanPlay);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('durationchange', onDur);
      v.removeEventListener('play', onPlayEv);
      v.removeEventListener('pause', onPauseEv);
      v.removeEventListener('ended', onEndedEv);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('canplay', onCanPlay);
    };
  }, [onTimeUpdate, onLoadedMetadata, onDurationChange, onPlay, onPause, onEnded]);

  // Sync muted/volume into the underlying element.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
    v.volume = isMuted ? 0 : volume;
  }, [isMuted, volume]);

  // Fullscreen state listener.
  useEffect(() => {
    const handler = () => setIsFullscreen(document.fullscreenElement === containerRef.current);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Track container width so the player can swap between compact (card)
  // and full (large stage / fullscreen) control layouts automatically.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const w = entry.contentRect ? entry.contentRect.width : el.clientWidth;
      setContainerWidth(w);
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // ── Active-player registration for keyboard routing ──────────────
  const markActive = useCallback(() => {
    activePlayerId = idRef.current;
  }, []);

  useEffect(() => {
    markActive();
    return () => { if (activePlayerId === idRef.current) activePlayerId = null; };
  }, [markActive]);

  // ── Keyboard shortcuts (active when this player is primary) ─────
  useEffect(() => {
    const handler = (e) => {
      if (activePlayerId !== idRef.current) return;
      // Skip when typing into a field.
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const v = videoRef.current;
      if (!v) return;
      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          e.preventDefault();
          // eslint-disable-next-line no-use-before-define
          togglePlay();
          revealControls();
          break;
        case 'f':
        case 'F':
          e.preventDefault();
          // eslint-disable-next-line no-use-before-define
          toggleFullscreen();
          break;
        case 'm':
        case 'M':
          e.preventDefault();
          setIsMuted((m) => !m);
          revealControls();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          try { v.currentTime = Math.max(0, v.currentTime - 10); } catch (err) { /* noop */ }
          revealControls();
          break;
        case 'ArrowRight':
          e.preventDefault();
          try { v.currentTime = Math.min(duration || v.currentTime + 10, v.currentTime + 10); } catch (err) { /* noop */ }
          revealControls();
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume((vv) => Math.min(1, vv + 0.1));
          setIsMuted(false);
          revealControls();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume((vv) => Math.max(0, vv - 0.1));
          revealControls();
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [duration, revealControls]);

  // ── Controls handlers ───────────────────────────────────────────
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    markActive();
    if (v.paused || v.ended) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      v.pause();
    }
  }, [markActive]);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement === el) {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
    } else if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    }
  }, []);

  const skipBy = useCallback((delta) => {
    const v = videoRef.current;
    if (!v) return;
    try {
      v.currentTime = Math.max(0, Math.min(duration || v.currentTime + delta, v.currentTime + delta));
    } catch (e) { /* noop */ }
  }, [duration]);

  // ── Progress bar interactions ───────────────────────────────────
  const computeProgressTime = useCallback((clientX) => {
    const el = progressRef.current;
    if (!el || !duration) return 0;
    const rect = el.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return ratio * duration;
  }, [duration]);

  const seekTo = useCallback((time) => {
    const v = videoRef.current;
    if (!v) return;
    try { v.currentTime = Math.max(0, Math.min(duration || time, time)); } catch (e) { /* noop */ }
  }, [duration]);

  const handleProgressClick = useCallback((e) => {
    seekTo(computeProgressTime(e.clientX));
    revealControls();
  }, [computeProgressTime, seekTo, revealControls]);

  const handleProgressMouseDown = useCallback((e) => {
    scrubbingRef.current = true;
    setScrubbing(true);
    seekTo(computeProgressTime(e.clientX));
    const onMove = (ev) => {
      const t = computeProgressTime(ev.clientX);
      seekTo(t);
      setHover({
        x: Math.max(80, Math.min(ev.clientX - (progressRef.current?.getBoundingClientRect().left || 0), (progressRef.current?.getBoundingClientRect().width || 1) - 80)),
        time: t,
      });
    };
    const onUp = () => {
      scrubbingRef.current = false;
      setScrubbing(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      armHideTimer();
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [computeProgressTime, seekTo, armHideTimer]);

  // Hover-scrub thumbnail (option (b) — hidden video seeks on hover).
  const drawThumbFrame = useCallback(() => {
    const tv = thumbVideoRef.current;
    const c = thumbCanvasRef.current;
    if (!tv || !c) return;
    try {
      const ctx = c.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(tv, 0, 0, c.width, c.height);
    } catch (e) { /* CORS or not-ready — skip */ }
  }, []);

  useEffect(() => {
    const tv = thumbVideoRef.current;
    if (!tv) return undefined;
    const onSeeked = () => drawThumbFrame();
    tv.addEventListener('seeked', onSeeked);
    return () => tv.removeEventListener('seeked', onSeeked);
  }, [drawThumbFrame]);

  const handleProgressMouseMove = useCallback((e) => {
    if (!duration) return;
    const rect = progressRef.current?.getBoundingClientRect();
    if (!rect) return;
    const localX = e.clientX - rect.left;
    const time = computeProgressTime(e.clientX);
    setHover({
      x: Math.max(80, Math.min(localX, rect.width - 80)),
      time,
    });
    const now = performance.now();
    if (now - lastSeekRef.current >= HOVER_SEEK_THROTTLE_MS) {
      lastSeekRef.current = now;
      const tv = thumbVideoRef.current;
      if (tv) {
        try { tv.currentTime = Math.max(0, Math.min(duration - 0.05, time)); } catch (err) { /* noop */ }
      }
    }
  }, [computeProgressTime, duration]);

  const handleProgressMouseLeave = useCallback(() => {
    setHover(null);
  }, []);

  // ── Render ──────────────────────────────────────────────────────
  const progress = duration ? Math.min(currentTime, duration) / duration : 0;
  const buffered = duration ? Math.min(bufferedEnd, duration) / duration : 0;
  const volLevel = isMuted ? 0 : volume;

  const containerStyle = {};
  if (!isFullscreen && !fillHeight) containerStyle.aspectRatio = aspectRatio;

  const rootClass = [
    styles.root,
    !isFullscreen && !fillHeight ? styles.aspect : '',
    fillHeight ? styles.fillHeight : '',
    !showControls ? styles.rootHideControls : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      ref={containerRef}
      className={rootClass}
      style={containerStyle}
      onMouseMove={revealControls}
      onMouseEnter={() => { markActive(); revealControls(); }}
      onMouseLeave={() => { if (isPlaying) setShowControls(false); }}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay={autoPlay}
        playsInline={playsInline}
        preload="metadata"
        className={styles.video}
      />
      {/* Hidden duplicate video for hover-scrub thumbnails. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={thumbVideoRef}
        src={src}
        muted
        playsInline
        preload="metadata"
        className={styles.thumbVideo}
      />

      {/* Click-anywhere-to-toggle layer. */}
      <button
        type="button"
        className={styles.clickCatch}
        onClick={togglePlay}
        onDoubleClick={toggleFullscreen}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        tabIndex={-1}
      />

      {/* Big centered play overlay when paused. */}
      {!isPlaying && !isBuffering && (
        <div className={styles.centerPlay}>
          <button
            type="button"
            className={styles.centerPlayBtn}
            onClick={togglePlay}
            aria-label="Play"
          >
            <PlayIcon />
          </button>
        </div>
      )}

      {/* Buffer spinner. */}
      {isBuffering && (
        <div className={styles.bufferOverlay}>
          <div className={styles.spinner} />
        </div>
      )}

      {/* Bottom controls — two variants.
          • COMPACT (card-size, not fullscreen): play/pause + fullscreen only,
            with a 3px progress strip pinned to the bottom edge.
          • FULL (large stage / fullscreen): rich controls — scrub with hover
            thumbnails, skip-10, time, volume, fullscreen. */}
      {isCompact ? (
        <div
          className={`${styles.controlsWrap} ${styles.controlsWrapCompact}`}
          onMouseEnter={revealControls}
          onMouseMove={revealControls}
        >
          <div className={styles.miniBtnRow}>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            <div className={styles.spacer} />
            <button
              type="button"
              className={styles.iconBtn}
              onClick={toggleFullscreen}
              aria-label="Fullscreen"
            >
              <FullscreenEnterIcon />
            </button>
          </div>
          {/* Thin progress strip pinned to the bottom edge of the canvas.
              Clickable for a quick seek; no scrub thumb / hover thumbnails
              at this size — it's primarily a visual indicator. */}
          <div
            ref={progressRef}
            className={styles.miniProgressRow}
            onClick={handleProgressClick}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') { e.preventDefault(); skipBy(-5); } else if (e.key === 'ArrowRight') { e.preventDefault(); skipBy(5); } else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlay(); }
            }}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={currentTime}
            tabIndex={0}
          >
            <div className={styles.miniProgressBuffered} style={{ width: `${buffered * 100}%` }} />
            <div className={styles.miniProgressPlayed} style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      ) : (
        <div
          className={styles.controlsWrap}
          onMouseEnter={revealControls}
          onMouseMove={revealControls}
        >
          {/* Progress bar */}
          <div
            ref={progressRef}
            className={`${styles.progressRow} ${scrubbing ? styles.scrubbing : ''}`}
            onMouseMove={handleProgressMouseMove}
            onMouseLeave={handleProgressMouseLeave}
            onMouseDown={handleProgressMouseDown}
            onClick={handleProgressClick}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft') { e.preventDefault(); skipBy(-5); } else if (e.key === 'ArrowRight') { e.preventDefault(); skipBy(5); } else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePlay(); }
            }}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={duration || 0}
            aria-valuenow={currentTime}
            tabIndex={0}
          >
            <div className={styles.progressTrack}>
              <div className={styles.progressBuffered} style={{ width: `${buffered * 100}%` }} />
              <div className={styles.progressPlayed} style={{ width: `${progress * 100}%` }} />
            </div>
            <div className={styles.progressThumb} style={{ left: `${progress * 100}%` }} />
            {hover && (
              <div className={styles.thumbTooltip} style={{ left: hover.x }}>
                <canvas ref={thumbCanvasRef} width={160} height={90} className={styles.thumbCanvas} />
                <span className={styles.thumbTime}>{formatTime(hover.time)}</span>
              </div>
            )}
          </div>

          {/* Buttons row */}
          <div className={styles.btnRow}>
            <button type="button" className={styles.iconBtn} onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
            <button type="button" className={styles.iconBtn} onClick={() => skipBy(-10)} aria-label="Back 10 seconds">
              <Skip10BackIcon />
            </button>
            <button type="button" className={styles.iconBtn} onClick={() => skipBy(10)} aria-label="Forward 10 seconds">
              <Skip10FwdIcon />
            </button>
            <span className={styles.timeLabel}>
              {`${formatTime(currentTime)} / ${formatTime(duration)}`}
            </span>
            <div className={styles.spacer} />
            <div className={styles.volWrap}>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setIsMuted((m) => !m)}
                aria-label={isMuted ? 'Unmute' : 'Mute'}
              >
                <VolumeIcon muted={isMuted} level={volLevel} />
              </button>
              <div
                className={styles.volSlider}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
                  setVolume(ratio);
                  setIsMuted(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                    e.preventDefault();
                    setVolume((vv) => Math.max(0, vv - 0.1));
                  } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                    e.preventDefault();
                    setVolume((vv) => Math.min(1, vv + 0.1));
                    setIsMuted(false);
                  }
                }}
                role="slider"
                aria-label="Volume"
                aria-valuemin={0}
                aria-valuemax={1}
                aria-valuenow={volLevel}
                tabIndex={-1}
              >
                <div
                  className={styles.volFill}
                  style={{ transform: `scaleX(${volLevel})` }}
                />
              </div>
            </div>
            <button
              type="button"
              className={styles.iconBtn}
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <FullscreenExitIcon /> : <FullscreenEnterIcon />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default NetflixPlayer;
